import { computed, onBeforeUnmount, onMounted, ref, toValue } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';

import { useEventHub } from '@satellite/composables/useEventHub';
import yardViewService from '@satellite/services/yard-view-service';

import { DEFAULT_INVALIDATE_OPTIONS, queryKeys } from './queryKeys';

import type { ComputedRef, Ref } from 'vue';
import type { Maybe } from '@nova/core';
import type { InventoryCounts } from '@satellite/helpers/yardInventoryCounts';

export interface YardInventoryCountsAreaRow {
  spotAreaId: string;
  spotAreaName: string;
  count: number;
}

export interface YardInventoryCountsResponse {
  missing: number;
  waiting: number;
  total: number;
  byArea: YardInventoryCountsAreaRow[];
}

const EMPTY_RESPONSE: YardInventoryCountsResponse = Object.freeze({
  missing: 0,
  waiting: 0,
  total: 0,
  byArea: []
});

// Container moves (assignments), status changes (missing/arrived), container create/delete, and
// spot-area changes (a new/renamed/deactivated area shifts the per-area buckets) all change counts.
const INVALIDATING_EVENTS = [
  'create-AssetContainer',
  'update-AssetContainer',
  'delete-AssetContainer',
  'create-AssetContainerEvent',
  'update-AssetContainerEvent',
  'delete-AssetContainerEvent',
  'create-SpotAssignment',
  'update-SpotAssignment',
  'delete-SpotAssignment',
  'create-SpotArea',
  'update-SpotArea',
  'delete-SpotArea'
] as const;

export interface UseYardInventoryCountsOptions {
  warehouseId: Ref<Maybe<string>> | ComputedRef<Maybe<string>> | (() => Maybe<string>);
  /** Bypass viewport gating when there is no mount/intersection lifecycle. */
  headless?: boolean;
  mode?: 'live';
  staleTime?: number;
}

/**
 * Warehouse-scoped inventory counts (per active spot area + missing + waiting) from the
 * `yard/view/inventory-counts` BFF, via TanStack Query with EventHub live invalidation. One cached
 * query per warehouse feeds the create-task picker and every inventory card — cards sum their stored
 * groups against `counts` with `totalAssetsForGroups`, so there is no per-task request.
 */
export function useYardInventoryCounts(options: UseYardInventoryCountsOptions) {
  const { warehouseId, headless = false, mode = 'live', staleTime = Infinity } = options;
  const eventHub = useEventHub();
  const queryClient = useQueryClient();

  const effectiveWarehouseId = computed<Maybe<string>>(() => toValue(warehouseId) || null);
  const queryKey = computed(() =>
    effectiveWarehouseId.value ? queryKeys.yardInventoryCounts(effectiveWarehouseId.value) : []
  );

  const isInViewport = ref(headless);
  const pendingInvalidation = ref(false);

  const inventoryCountsQuery = useQuery({
    queryKey,
    queryFn: async ({ signal }): Promise<YardInventoryCountsResponse> => {
      const id = effectiveWarehouseId.value;
      if (!id) {
        return EMPTY_RESPONSE;
      }
      const response = await yardViewService.getInventoryCounts(id, {
        requestConfig: { signal },
        enableCache: false
      });
      return {
        missing: Number(response?.missing) || 0,
        waiting: Number(response?.waiting) || 0,
        total: Number(response?.total) || 0,
        byArea: Array.isArray(response?.byArea)
          ? response.byArea.map((row: YardInventoryCountsAreaRow) => ({
              spotAreaId: String(row.spotAreaId),
              spotAreaName: String(row.spotAreaName ?? ''),
              count: Number(row.count) || 0
            }))
          : []
      };
    },
    enabled: computed(() => Boolean(effectiveWarehouseId.value) && isInViewport.value),
    staleTime,
    refetchOnWindowFocus: false
  });

  const data = computed<YardInventoryCountsResponse>(
    () => inventoryCountsQuery.data.value ?? EMPTY_RESPONSE
  );

  const areas = computed<YardInventoryCountsAreaRow[]>(() => data.value.byArea);
  const missing = computed(() => data.value.missing);
  const waiting = computed(() => data.value.waiting);

  /** `InventoryCounts` shape consumed by `totalAssetsForGroups`. */
  const counts = computed<InventoryCounts>(() => ({
    byArea: new Map(data.value.byArea.map(row => [row.spotAreaId, row.count])),
    missing: data.value.missing,
    waiting: data.value.waiting
  }));

  const isLoading = computed(() => inventoryCountsQuery.isLoading.value);
  const isFetching = computed(() => inventoryCountsQuery.isFetching.value);
  const error = computed(() => inventoryCountsQuery.error.value);

  const invalidate = () => {
    if (queryKey.value.length) {
      queryClient.invalidateQueries({ queryKey: queryKey.value }, DEFAULT_INVALIDATE_OPTIONS);
    }
  };

  const refresh = async () => {
    await inventoryCountsQuery.refetch();
  };

  const onIntersect = (entry?: { isIntersecting?: boolean } | boolean) => {
    if (headless) {
      return;
    }
    const isIntersecting = typeof entry === 'boolean' ? entry : entry?.isIntersecting ?? true;
    isInViewport.value = isIntersecting;
    if (isIntersecting && pendingInvalidation.value) {
      pendingInvalidation.value = false;
      invalidate();
    }
  };

  const handleInvalidatingEvent = (event?: { warehouseId?: Maybe<string> }) => {
    // Scope to this warehouse; legacy payloads without a warehouseId fall through.
    if (
      effectiveWarehouseId.value &&
      event?.warehouseId &&
      event.warehouseId !== effectiveWarehouseId.value
    ) {
      return;
    }
    if (!isInViewport.value) {
      pendingInvalidation.value = true;
      return;
    }
    invalidate();
  };

  onMounted(() => {
    if (mode === 'live') {
      INVALIDATING_EVENTS.forEach(name => eventHub.on(name, handleInvalidatingEvent));
    }
  });

  onBeforeUnmount(() => {
    if (mode === 'live') {
      INVALIDATING_EVENTS.forEach(name => eventHub.off(name, handleInvalidatingEvent));
    }
  });

  return {
    data,
    areas,
    missing,
    waiting,
    counts,

    isLoading,
    isFetching,
    error,

    refresh,
    invalidate,
    onIntersect,

    query: inventoryCountsQuery
  };
}
